import React, { useEffect, useRef, useState } from "react";
import { AlertContextProvider } from "@actinc/dls/context/AlertContext/provider";
import { ConfirmProvider } from "@actinc/dls/context/Confirm/confirmProvider";
import { MathJaxContext } from "better-react-mathjax/esm";
import { LicenseInfo } from "@mui/x-license-pro";
import {
  AccountCircle as AccountCircleIcon,
  ChevronDown as ChevronDownIcon,
} from "@actinc/dls/icons";
import { Loading, SessionTimer } from "@actinc/dls/components";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { parse } from "papaparse";
import {
  Link as RouterLink,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useClient } from "urql";

import { AdminPrograms } from "./routes/Admin/Programs";
import { AdminProgramMetafiles } from "./routes/Admin/Programs/Metafiles";
import { AdminProgramReviewGroups } from "./routes/Admin/Programs/ReviewGroups";
import { AdminUsers } from "./routes/Admin/Users";
import { BatchDataUpload } from "./routes/BulkProcesses/BatchDataUpload";
import { BatchUpdateContent } from "./routes/Admin/BatchUpdateContent";
import { CreateContent } from "./routes/BulkProcesses/CreateContent";
import { DisplayMetadataList } from "./routes/DisplayMetadataList";
import { PoolTasks } from "./routes/Dashboard/Pool";
import { PreviewContent } from "./routes/PreviewContent";
import { ImportContent } from "./routes/BulkProcesses/ImportContent";
import { SearchItems } from "./routes/Search/Items";
import { SearchPassages } from "./routes/Search/Passages";
import { SearchTasks } from "./routes/Search/Tasks";
import { TaskDetails } from "./routes/Workspace/TaskDetails";
import { UserTasks } from "./routes/Dashboard/Tasks";
import { preventUnload, Workspace } from "./routes/Workspace";

import {
  MetadataConfigurationContext,
  useMetadataConfigurationContext,
} from "./metadataConfigurationProvider";
import { ProgramsContext, useProgramsContext } from "./programsProvider";
import { StandardsContext } from "./standardsProvider";
import { UserContext, useUserProfileContext } from "./userProvider";
import { UsersContext, useUsersContext } from "./usersProvider";

import "../node_modules/react-grid-layout/css/styles.css";
import "../node_modules/react-resizable/css/styles.css";
import "./styles.css";

import Cookies from "js-cookie";
import { TTSTagStatusProvider } from "./providers/TTSFileStatusProvider";

LicenseInfo.setLicenseKey(
  "c75e846c1efe69fee0209e3c37f9d218Tz0xMjI5OTQsRT0xNzk2OTQ3MTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=",
);

// State variables
const navigation = [
  {
    label: "Tasks",
    options: [
      {
        href: "/",
        label: "My Tasks",
      },
      {
        filter: ({ programs }) =>
          programs.some((program) =>
            program._actions.includes("managePoolTasks"),
          ),
        href: "/pool",
        label: "Pool Tasks",
      },
    ],
  },
  {
    label: "Search",
    options: [
      {
        filter: ({ programs }) =>
          programs.some((program) =>
            program._actions.includes("searchContent"),
          ),
        href: "/search/items",
        label: "Items",
      },
      {
        filter: ({ programs }) =>
          programs.some((program) =>
            program._actions.includes("searchContent"),
          ),
        href: "/search/passages",
        label: "Passages",
      },
      {
        filter: ({ programs }) =>
          programs.some((program) =>
            program._actions.includes("searchContent"),
          ),
        href: "/search/tasks",
        label: "Tasks",
      },
    ],
  },
  {
    label: "Bulk Processes",
    options: [
      {
        filter: ({ programs }) =>
          programs.some((program) =>
            program._actions.includes("createContent"),
          ),
        href: "/content/create",
        label: "Create Content",
      },
      {
        filter: ({ programs }) =>
          programs.some((program) =>
            program._actions.includes("batchDataUpload"),
          ),
        href: "/batch-data-upload/item",
        label: "Item Batch Data Upload",
      },
      {
        filter: ({ programs }) =>
          programs.some((program) =>
            program._actions.includes("batchDataUpload"),
          ),
        href: "/batch-data-upload/passage",
        label: "Passage Batch Data Upload",
      },
      {
        filter: ({ programs }) =>
          programs.some((program) => program._actions.includes("import")),
        href: "/import-content",
        label: "Import Content",
      },
    ],
  },
  {
    label: "Admin",
    options: [
      {
        filter: ({ user }) => user._actions.includes("batchUpdateContent"),
        href: "/admin/batchUpdateContent",
        label: "Batch Update Content",
      },
      {
        filter: ({ user }) => user._actions.includes("managePrograms"),
        href: "/admin/programs",
        label: "Programs",
      },
      {
        filter: ({ user }) => user._actions.includes("manageUsers"),
        href: "/admin/users",
        label: "Users",
      },
    ],
  },
];
const initialMenuElState = navigation.reduce(
  (acc, option) => ({ ...acc, [option.label]: null }),
  {},
);
const isProduction = window.location.hostname === "cde.act.org";
const promptWithMsRemaining = 10 * 1000; // 10 seconds
const sessionTimeout = (isProduction ? 15 : 60) * 60 * 1000; // 15 minutes - time until the user is forcibly logged out
const tokenMaxAgeMs = (isProduction ? 10 : 55) * 60 * 1000; // 10 minutes - time until dialog appears
// const sessionTimeout = 5 * 60 * 1000; // 5 minutes - time until the user is forcibly logged out
// const tokenMaxAgeMs = 4 * 60 * 1000; // 4 minutes - time until dialog appears

// Get the Date value at which the session will expire
const getSessionExpireTime = () => new Date().valueOf() + sessionTimeout;

const App = () => {
  const client = useClient();
  const navigate = useNavigate();

  const [metadataConfiguration] = useMetadataConfigurationContext(client);
  const [programs] = useProgramsContext(client);
  const [user] = useUserProfileContext(client);
  const [users] = useUsersContext(client);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [navAnchorEl, setNavAnchorEl] = useState(initialMenuElState);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [sessionExpiresAt, setSessionExpiresAt] =
    useState(getSessionExpireTime);

  const [buildInfo, setBuildInfo] = useState(null);
  useEffect(() => {
    if (!buildInfo) {
      let lastModified;
      fetch("/buildInfo.json", { method: "HEAD" }).then((response) => {
        lastModified =
          response.headers.get("last-modified") ||
          response.headers.get("Last-Modified");
        console.log(JSON.stringify(lastModified, null, 2));
      });

      fetch("/buildInfo.json")
        .then((response) => response.json())
        .then((data) => setBuildInfo({ ...data, Date: lastModified }));
    }
  }, [buildInfo]);

  const [standards, setStandards] = useState(null);
  useEffect(() => {
    if (standards) return;
    parse("/standards.csv", {
      complete: (results) => {
        setStandards(
          results.data.map((row) => ({
            documentSubject: row[0],
            fullStatement: row[2],
            humanCodingScheme: row[1],
          })),
        );
      },
      delimiter: ",",
      download: true,
      skipEmptyLines: true,
    });
  }, []);

  // Refresh the sessionExpiresAt value
  const refreshSession = () => {
    const expireTime = getSessionExpireTime();
    setSessionExpiresAt(expireTime);
    return expireTime;
  };

  // Set the initial value in localStorage
  useEffect(() => {
    const expireTime = getSessionExpireTime();
    console.info(
      "Session: %s initial value - %s",
      new Date().toISOString(),
      expireTime,
    );
    localStorage.setItem("refreshSession", expireTime);
  }, []);

  // Listens for storage events *from other documents* https://developer.mozilla.org/en-US/docs/Web/API/StorageEvent
  useEffect(() => {
    const listener = (e) => {
      // Only deal with relevant storage events
      if (e.key !== "refreshSession") return;

      console.info(
        "Session: %s storage event: %o",
        new Date().toISOString(),
        e.newValue,
      );

      // If the key is being removed, we're logging out
      if (e.newValue === null) {
        window.removeEventListener("beforeunload", preventUnload);
        return window.location.replace("/logout.html");
      }

      // If we're still here, we're refreshing
      refreshSession();

      // If the continue session dialog has popped, the sessionExpiresAt prop
      // won't update; we have to click the button programatically
      [...document.querySelectorAll("button")]
        .find(
          (button) => button.textContent.toLowerCase() === "continue session",
        )
        ?.click();
    };
    window.addEventListener("storage", listener, { passive: true });
    return () => window.removeEventListener("storage", listener);
  }, []);

  const token = Cookies.get("TOKEN");
  if (!token && window.location.hostname !== "localhost") {
    window.location.replace("/logout.html");
  }

  // Functions
  const handleNavMenuClick = (e) => {
    setNavAnchorEl({
      ...initialMenuElState,
      [e.currentTarget.textContent]: e.currentTarget,
    });
  };

  const handleNavMenuClose = () => {
    setNavAnchorEl(initialMenuElState);
  };

  const handleProfileMenuClick = (e) => {
    setProfileAnchorEl(e.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const onExpire = (e) => {
    console.info("Session: %s expired", new Date().toISOString());
    window.removeEventListener("beforeunload", preventUnload);
    window.location.replace("/logout.html");
  };

  const onKeepAlive = (e) => {
    console.info("Session: %s keepAlive", new Date().toISOString());
    const expireTime = refreshSession();
    localStorage.setItem("refreshSession", expireTime);
  };

  const loading =
    !user ||
    programs === null ||
    !metadataConfiguration ||
    !standards ||
    !users;

  const hasNoRolesAssigned =
    !loading &&
    Array.isArray(programs) &&
    programs.length === 0 &&
    !user?.superAdmin;

  useEffect(() => {
    if (hasNoRolesAssigned) {
      const timer = setTimeout(() => {
        localStorage.removeItem("refreshSession");
        window.location.replace("/logout.html");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [hasNoRolesAssigned]);

  return (
    <>
      <MathJaxContext version={2}>
        <SessionTimer
          expiresAt={new Date(sessionExpiresAt)}
          onExpire={onExpire}
          onKeepAlive={onKeepAlive}
          promptWithMsRemaining={promptWithMsRemaining}
          tokenMaxAgeMs={tokenMaxAgeMs}
        />
        <UserContext.Provider value={user}>
          <ConfirmProvider>
            <AlertContextProvider>
              <ProgramsContext.Provider value={programs}>
                <MetadataConfigurationContext.Provider
                  value={metadataConfiguration}
                >
                  <StandardsContext.Provider value={standards}>
                    <UsersContext.Provider value={users}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TTSTagStatusProvider>
                          {loading && <Loading />}
                          {!loading && hasNoRolesAssigned && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "100vh",
                              }}
                            >
                              <Typography variant="h6" color="text.secondary">
                                No roles have been assigned to your account.
                                Please contact an administrator.
                              </Typography>
                            </Box>
                          )}
                          {!loading && !hasNoRolesAssigned && (
                            <>
                              <AppBar color="primary" position="sticky">
                                <Toolbar
                                  sx={{
                                    justifyContent: "space-between",
                                  }}
                                  variant="dense"
                                >
                                  <Box sx={{ display: "flex" }}>
                                    <Box
                                      sx={{
                                        marginTop: 1,
                                      }}
                                    >
                                      <RouterLink to="/">
                                        <img
                                          alt="ACT Logo"
                                          src="/login/logo-act-rev-300.png"
                                          width="85px"
                                          height="22px"
                                        />
                                      </RouterLink>
                                    </Box>
                                    {user &&
                                      navigation.map((button) => {
                                        const options = button.options.filter(
                                          (option) =>
                                            !option.filter ||
                                            option.filter({ programs, user }),
                                        );
                                        if (!options.length) return null;
                                        const id = `navigation-menu-${button.label}`;
                                        return (
                                          <React.Fragment
                                            key={`${button.label}`}
                                          >
                                            <Button
                                              aria-controls={id}
                                              aria-haspopup="true"
                                              aria-label={
                                                button.label + " menu"
                                              }
                                              color="inherit"
                                              edge="start"
                                              endIcon={<ChevronDownIcon />}
                                              key={button.label + "Button"}
                                              onClick={handleNavMenuClick}
                                              size="small"
                                              sx={{
                                                backgroundColor: "primary.dark",
                                                marginLeft: 2,
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {button.label}
                                            </Button>
                                            <Menu
                                              id={id}
                                              anchorEl={
                                                navAnchorEl[button.label]
                                              }
                                              key={button.label + "Menu"}
                                              open={Boolean(
                                                navAnchorEl[button.label],
                                              )}
                                              onClose={handleNavMenuClose}
                                              PaperProps={{
                                                style: {
                                                  minWidth: "150px",
                                                },
                                              }}
                                              variant="menu"
                                            >
                                              {options.map((option) => (
                                                <MenuItem
                                                  color="inherit"
                                                  component={RouterLink}
                                                  dense={true}
                                                  key={`${button.label}${option.label}MenuItem`}
                                                  onClick={handleNavMenuClose}
                                                  reloadDocument
                                                  to={{
                                                    pathname: option.href,
                                                    state: {
                                                      from: location.pathname,
                                                    },
                                                  }}
                                                  underline="none"
                                                  variant="button"
                                                >
                                                  {option.label}
                                                </MenuItem>
                                              ))}
                                            </Menu>
                                          </React.Fragment>
                                        );
                                      })}
                                  </Box>
                                  <Box sx={{ display: "flex" }}>
                                    {user ? (
                                      <>
                                        <IconButton
                                          aria-controls="profile-menu"
                                          aria-haspopup="true"
                                          aria-label="account of current user"
                                          onClick={handleProfileMenuClick}
                                          color="inherit"
                                          size="small"
                                        >
                                          <AccountCircleIcon />
                                        </IconButton>
                                        <Menu
                                          id="profile-menu"
                                          anchorEl={profileAnchorEl}
                                          anchorOrigin={{
                                            vertical: "bottom",
                                            horizontal: "right",
                                          }}
                                          keepMounted
                                          transformOrigin={{
                                            vertical: "top",
                                            horizontal: "right",
                                          }}
                                          open={Boolean(profileAnchorEl)}
                                          onClose={handleProfileMenuClose}
                                        >
                                          <MenuItem
                                            color="inherit"
                                            component={Link}
                                            dense={true}
                                            href="/logout.html"
                                            onClick={handleProfileMenuClose}
                                            underline="none"
                                            variant="button"
                                          >
                                            Logout
                                          </MenuItem>
                                          <MenuItem
                                            color="inherit"
                                            component={Typography}
                                            dense={true}
                                            disabled={true}
                                            underline="none"
                                          >
                                            {[
                                              user.firstName,
                                              user.lastName,
                                            ].join(" ")}
                                          </MenuItem>
                                          <MenuItem
                                            color="inherit"
                                            component={Typography}
                                            dense={true}
                                            disabled={true}
                                            underline="none"
                                          >
                                            {`Build #: ${buildInfo?.RC} - Date: ${buildInfo?.Date}`}
                                          </MenuItem>
                                        </Menu>
                                      </>
                                    ) : (
                                      <Typography variant="h6" color="error">
                                        <Link href="/login.html">Login</Link>
                                      </Typography>
                                    )}
                                  </Box>
                                </Toolbar>
                              </AppBar>
                              <Routes>
                                <Route
                                  element={<BatchUpdateContent />}
                                  path="/admin/batchUpdateContent"
                                />
                                <Route
                                  element={<AdminPrograms />}
                                  path="/admin/programs"
                                />
                                <Route
                                  path="/admin/programs/:programID/metafiles"
                                  element={<AdminProgramMetafiles />}
                                />
                                <Route
                                  path="/admin/programs/:programID/reviewGroups"
                                  element={<AdminProgramReviewGroups />}
                                />
                                <Route
                                  element={<AdminUsers />}
                                  path="/admin/users"
                                />
                                <Route
                                  element={<BatchDataUpload />}
                                  path="/batch-data-upload/:objectType"
                                />
                                <Route
                                  element={<DisplayMetadataList />}
                                  path="/display-metadata-list"
                                />
                                <Route
                                  element={<CreateContent />}
                                  path="/content/create"
                                />
                                <Route
                                  element={<ImportContent />}
                                  path="/import-content/"
                                />
                                <Route
                                  element={<SearchItems />}
                                  path="/search/items"
                                />
                                <Route
                                  element={<SearchPassages />}
                                  path="/search/passages"
                                />
                                <Route
                                  element={<SearchTasks />}
                                  path="/search/tasks"
                                />
                                <Route
                                  element={<TaskDetails />}
                                  path="/task-details/:objectType/:objectId/:taskId"
                                />
                                <Route
                                  element={<Workspace />}
                                  path="/workspace"
                                />
                                <Route path="/logout.html" />
                                <Route element={<UserTasks />} path="/" />
                                <Route element={<PoolTasks />} path="/pool" />
                                <Route
                                  element={<PreviewContent />}
                                  path="/preview-content"
                                />
                              </Routes>
                            </>
                          )}
                        </TTSTagStatusProvider>
                      </LocalizationProvider>
                    </UsersContext.Provider>
                  </StandardsContext.Provider>
                </MetadataConfigurationContext.Provider>
              </ProgramsContext.Provider>
            </AlertContextProvider>
          </ConfirmProvider>
        </UserContext.Provider>
      </MathJaxContext>
    </>
  );
};

export default App;
